import styles from './Avatar.module.css';

export interface AvatarProps {
    /** The person's full name. Shown as initials, read in full by a screen reader. */
    readonly name: string;
    readonly 'data-testid'?: string;
}

/**
 * First and last initial, uppercased. A single-word name yields one letter rather than two.
 *
 * Only words that start with a letter count, so a bracket, a digit or a quote is never an initial:
 * "Rep (test)" is "R", not "R(" (BUG-286), and a name with no such word yields nothing.
 *
 * Exported for its own test: initial-derivation is the part of this component that has edge cases
 * (one word, extra whitespace, an empty string), and testing it through the DOM would only ever
 * exercise the happy path.
 */
export function initialsOf(name: string): string {
    const parts = name
        .trim()
        .split(/\s+/)
        .filter((part) => /^\p{L}/u.test(part));
    const first = parts.at(0);
    if (first === undefined) {
        return '';
    }
    const last = parts.length > 1 ? parts.at(-1) : undefined;
    return `${first.charAt(0)}${last?.charAt(0) ?? ''}`.toUpperCase();
}

/**
 * §5's Avatar — a gold circle carrying initials.
 *
 * `role="img"` with the full name as its accessible name, rather than letting the initials be read.
 * A screen reader announcing "J P" is noise; announcing "Jane Perera" is the information the
 * initials stand in for. The role also makes the two letters presentational, so they are not read
 * twice.
 *
 * One size only, and it is `layout/control-h-compact`. §4 names no avatar size, and inventing a raw
 * pixel here would be the exact FS-3 NFR-1 violation the token layer exists to prevent — so the diameter
 * is pinned to the compact control height, which also puts an avatar on the same vertical rhythm as
 * the controls it sits beside. A size scale arrives when a screen needs a second size.
 */
export function Avatar({ name, 'data-testid': testId }: AvatarProps) {
    return (
        <span aria-label={name} className={styles.avatar} data-testid={testId} role="img">
            {initialsOf(name)}
        </span>
    );
}
